import { useApiQuery } from '../useApiQuery';
import { leadKeys } from '@/lib/queryKeys';
import { leadEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';

/**
 * Route a lead by service category — `categoryId` (a service-category id) OR a
 * free-text `category` keyword — to a destination team OR user.
 */
export interface LeadCategoryRoute {
  categoryId?: string;
  category?: string;
  teamId?: string;
  userId?: string;
}

/**
 * Route a lead by territory — exact `zip` OR a `zipPrefix` — to a destination
 * team OR user.
 */
export interface LeadTerritoryRoute {
  zip?: string;
  zipPrefix?: string;
  teamId?: string;
  userId?: string;
}

/** Route a lead by its source to a destination team OR user. */
export interface LeadSourceRoute {
  source: string;
  teamId?: string;
  userId?: string;
}

export interface LeadSourceSlaOverride {
  source: string;
  slaMinutes: number;
}

export interface LeadSettings {
  tenantId: string;
  defaultFollowUpDays: number;
  autoSetFollowUp: boolean;
  defaultSlaMinutes: number;
  slaUrgentMinutes: number;
  slaHighMinutes: number;
  slaLowMinutes: number;
  slaWarningThresholdPercent: number;
  slaBreachAction: 'NOTIFY_ONLY' | 'REASSIGN';
  slaEscalationUserId: string | null;
  sourceSlaOverrides: LeadSourceSlaOverride[];
  autoAssignEnabled: boolean;
  fallbackUserId: string | null;
  repCapacityLimit: number;
  honorWeeklyHours: boolean;
  categoryRoutes: LeadCategoryRoute[];
  territoryRoutes: LeadTerritoryRoute[];
  sourceRoutes: LeadSourceRoute[];
  notifyOnNewLead: boolean;
  notifyOnSlaWarning: boolean;
  notifyOnSlaBreach: boolean;
}

export function useLeadSettings() {
  return useApiQuery<LeadSettings>({
    queryKey: leadKeys.settings(),
    queryFn: () => apiGet<LeadSettings>(operationsClient, leadEndpoints.settings()),
    showErrorToast: false,
  });
}
